'use client'

import {
	SparklesIcon,
	HeartIcon,
	ArrowsPointingOutIcon
} from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/20/solid'
import DemoNavigation from '~/components/DemoNavigation'

const copy = {
	hero: {
		label: 'Storage Units',
		title: `Store <span class="text-binz-blue">smart</span> with BINZ. 
		<br />Your premier Cabo storage solution!`,
		description:
			`Convenient storage tailored to your Cabo lifestyle. BINZ offers storage units to fit your personalized needs including monthly and annual options.`,
	},
	features: [
		{
			name: 'Flexibility',
			description:
				'Various sizes available in 4-foot increments.',
			icon: ArrowsPointingOutIcon,
		},
		{
			name: 'Ease',
			description:
				'Industrial elevator, carts, and dollies to facilitate easy move in and out.',
			icon: SparklesIcon,
		},
		{
			name: 'Convenience',
			description:
				'Secure access to your storage unit during your scheduled time. ',
			icon: HeartIcon,
		},
	],
	pricing: {
		title: `Storage and pricing to fit your needs in a <span class='text-binz-blue'>secure</span>, <span class='text-binz-blue'>indoor</span>, and <span class='text-binz-blue'>temperature-controlled</span> facility.`,
		tiers: [
			{
				name: 'Startup',
				id: 'tier-startup',
				href: 'checkout',
				priceMonthly: 'Flexible pricing',
				features: [
					'Various sizes available',
					'Indoor, climate-controlled environment',
					'High level of security to protect your belongings',
				],
				mostPopular: true,
			},
		]
	},
	howItWorks: [
		{
			name: '1. Register for your storage unit',
			description:
				`To register, please click this <a class="text-binz-blue" href="">link</a>. You will be directed to choose the monthly/annual option as well as the size option to best fit your needs.`,
		},
		{
			name: '2. Schedule your move in date and time',
			description:
				'Upon registering, you will request a move in date and time. Team BINZ will confirm your dedicated time upon receiving your request. '
		},
		{
			name: '3. Move-in',
			description:
				'At your dedicated move in time, arrive at the BINZ bodega and a member of Team BINZ will assist you in finding your unit. BINZ has an elevator, carts and dollies available to assist you with your move in.',
		},
		{
			name: '4. Accessing your unit',
			description:
				'When you need to access your unit, schedule your visit on your dashboard. A member of Team BINZ will work with you to ensure we have the elevator, carts and dollies available.  ',
		}
	]
}


function classNames(...classes) {
	return classes.filter(Boolean).join(' ')
}

export default function Example() {

	return (
		<div className="bg-white">
			{/* Header */}
			<header className="absolute inset-x-0 top-0 z-50">
				<DemoNavigation />
			</header>

			<main className="isolate">
				{/* Feature section */}
				<div className="mx-auto mt-56 max-w-7xl px-6 sm:mt-56 lg:px-8">
					<div className="mx-auto max-w-2xl lg:text-center">
						<h2 className="text-base/7 font-semibold text-binz-blue" dangerouslySetInnerHTML={{ __html: copy.hero.label }}></h2>
						<p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-balance" dangerouslySetInnerHTML={{ __html: copy.hero.title }} />
						<p className="mt-6 text-pretty text-lg/8 text-gray-600" dangerouslySetInnerHTML={{ __html: copy.hero.description }} />
					</div>
					<div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
						<dl className="grid max-w-4xl mx-auto grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-3 lg:gap-y-16">
							{copy.features.map((feature) => (
								<div key={feature.name} className="relative pl-16">
									<dt className="text-base/7 font-semibold text-gray-900">
										<div className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-binz-blue">
											<feature.icon aria-hidden="true" className="h-6 w-6 text-white" />
										</div>
										{feature.name}
									</dt>
									<dd className="mt-2 text-base/7 text-gray-600">{feature.description}</dd>
								</div>
							))}
						</dl>
					</div>
				</div>

				{/* Pricing section */}
				<div className="py-24 sm:pt-24">
					<div className="mx-auto max-w-7xl px-6 lg:px-8">
						<div className="mx-auto max-w-7xl lg:text-center">
							<p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-balance" dangerouslySetInnerHTML={{ __html: copy.pricing.title }}>

							</p>
						</div>
						<div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-y-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
							{copy.pricing.tiers.map((tier, tierIdx) => (
								<div
									key={tier.id}
									className={classNames(
										tier.mostPopular ? 'lg:z-10 lg:rounded-b-none' : 'lg:mt-8',
										tierIdx === 0 ? 'lg:rounded-r-none' : '',
										'lg:rounded-l-none',
										'flex flex-col justify-between rounded-3xl bg-white p-8 ring-1 ring-gray-200 xl:p-10 col-start-2',
									)}
								>
									<div>
										<p className="mt-6 flex items-baseline gap-x-1">
											<span className="text-4xl font-semibold tracking-tight text-gray-900">{tier.priceMonthly}</span>
										</p>
										<ul role="list" className="mt-8 space-y-3 text-sm/6 text-gray-600">
											{tier.features.map((feature) => (
												<li key={feature} className="flex gap-x-3">
													<CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-binz-blue" />
													{feature}
												</li>
											))}
										</ul>
									</div>
									<a
										href={tier.href}
										aria-describedby={tier.id}
										className={classNames(
											tier.mostPopular
												? 'bg-binz-blue text-white shadow-sm hover:bg-binz-blue'
												: 'text-binz-blue ring-1 ring-inset ring-binz-blue hover:ring-binz-blue',
											'mt-8 block rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-binz-blue',
										)}
									>
										Buy plan
									</a>
								</div>
							))}
						</div>
					</div>
				</div>
				{/* Values section */}
				<div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-16 lg:px-8">
					<div className="mx-auto max-w-4xl text-center">
						<h2 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">How it works</h2>
					</div>
					<dl className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-x-8 gap-y-16 text-base/7 sm:grid-cols-2 lg:grid-cols-3">
						{copy.howItWorks.map((value) => (
							<div key={value.name}>
								<dt className="font-semibold text-gray-900">{value.name}</dt>
								<dd className="mt-1 text-gray-600" dangerouslySetInnerHTML={{ __html: value.description }}></dd>
							</div>
						))}
					</dl>
				</div>
				{/* Testimonial section */}
				<div className="mx-auto mt-24 max-w-7xl sm:mt-32 sm:px-6 lg:px-8">
					<div className="relative overflow-hidden bg-gray-900 px-6 py-20 shadow-xl sm:rounded-3xl sm:px-10 sm:py-24 md:px-12 lg:px-20">
						<img
							alt=""
							src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1473&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
							className="absolute inset-0 h-full w-full object-cover brightness-150 saturate-0"
						/>
						<div className="absolute inset-0 bg-gray-900/90 mix-blend-multiply" />
						<div aria-hidden="true" className="absolute -left-80 -top-56 transform-gpu blur-3xl">
							<div
								style={{
									clipPath:
										'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
								}}
								className="aspect-[1097/845] w-[68.5625rem] bg-gradient-to-r from-[#de1920] to-[#0d6bb6] opacity-[0.45]"
							/>
						</div>
						<div
							aria-hidden="true"
							className="hidden md:absolute md:bottom-16 md:left-[50rem] md:block md:transform-gpu md:blur-3xl"
						>
							<div
								style={{
									clipPath:
										'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
								}}
								className="aspect-[1097/845] w-[68.5625rem] bg-gradient-to-r from-[#de1920] to-[#0d6bb6] opacity-25"
							/>
						</div>
						<div className="relative mx-auto max-w-2xl lg:mx-0">
							<figure>
								<blockquote className="text-lg font-semibold text-white sm:text-xl/8">
									<p>
										“So glad we are using Binz now, it has made our future trips easy to plan and less packing. We come to Cabo at least 2 times a year so this will be so worth it. Great service, convenient and the binz are a great size and hold ALOT.”
									</p>
								</blockquote>
								<figcaption className="mt-6 text-base text-white">
									<div className="font-semibold">Denise B.</div>
								</figcaption>
							</figure>
						</div>
					</div>
				</div>

			</main>

		</div>
	)
}
